/**
 * PlanForm - Create/edit plan form with validation
 *
 * Features:
 * - React Hook Form with Zod validation
 * - Sections: Basic Info, Pricing, Limits (grouped), Features
 * - Dynamic features list with add/remove
 */
import { useState } from 'react';
import { cn } from '@respectly/ui';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Switch,
  Textarea,
} from '@respectly/ui';
import {
  Plus,
  Trash2,
  Save,
  X,
  DollarSign,
  FileText,
  MessageSquare,
  Mail,
  Sparkles,
  Loader2,
  Users,
  Contact,
  FileInput,
  Send,
} from 'lucide-react';

import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { SuperAdminPlan, CreatePlanDto } from '@respectly/shared-types';
import {
  parseStoredFeatures,
  stringifyStoredFeatures,
  formatCanonicalValue,
  shouldShowCanonical,
  CANONICAL_DEFAULT_LABELS,
  type CanonicalFeatureKey,
} from '@respectly/utils';

// ============================================
// Types
// ============================================

interface PlanFormProps {
  plan?: SuperAdminPlan;
  onSubmit: (data: CreatePlanDto) => Promise<boolean>;
  onCancel: () => void;
  isLoading?: boolean;
}

// ============================================
// Validation Schema
// ============================================

const customFeatureSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  value: z.string().optional(),
});

const labelOverridesSchema = z
  .object({
    sms: z.string().optional(),
    email: z.string().optional(),
    ai: z.string().optional(),
    users: z.string().optional(),
    contacts: z.string().optional(),
    forms: z.string().optional(),
  })
  .partial();

const planFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(500).optional(),
  monthlyPrice: z.coerce.number().min(0, 'Price must be positive'),
  annualPrice: z.coerce.number().min(0, 'Price must be positive'),
  trialDays: z.coerce.number().int().min(0).max(90),
  // Messaging
  smsCreditsPerMonth: z.coerce.number().int().min(0),
  emailCreditsPerMonth: z.coerce.number().int().min(0),
  aiCreditsPerMonth: z.coerce.number().int().min(0),
  // Team & Contacts
  maxUsers: z.coerce.number().int().min(0),
  maxContacts: z.coerce.number().int().min(0),
  maxForms: z.coerce.number().int().min(0),
  // Broadcasting
  maxSmsPerBroadcast: z.coerce.number().int().min(0),
  maxEmailPerBroadcast: z.coerce.number().int().min(0),
  customFeatures: z.array(customFeatureSchema),
  labelOverrides: labelOverridesSchema,
  isActive: z.boolean(),
  displayOrder: z.coerce.number().int().min(0),
  tierLevel: z.coerce.number().int().min(0).max(3).optional(),
  highlightLabel: z.string().max(50).optional().nullable(),
  badgeColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a hex color like #3B82F6')
    .optional()
    .nullable()
    .or(z.literal('')),
  recommendedForIndividual: z.boolean().optional(),
  recommendedForBusiness: z.boolean().optional(),
  requireCardForTrial: z.boolean().optional(),
});

type PlanFormData = z.infer<typeof planFormSchema>;

// ============================================
// Canonical feature preview ordering
// ============================================

const CANONICAL_ORDER: CanonicalFeatureKey[] = [
  'sms',
  'email',
  'ai',
  'users',
  'contacts',
  'forms',
];

// ============================================
// Sub-component: Limit Field
// ============================================

function LimitField({
  id,
  label,
  icon,
  register,
  error,
}: {
  id: keyof PlanFormData;
  label: string;
  icon: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any;
  error?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="flex items-center gap-1.5 text-sm">
        {icon}
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        min="0"
        {...register(id)}
        className={cn('h-8 text-sm', error && 'border-red-500')}
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ============================================
// Sub-component: Canonical Features Preview
// ============================================

const CANONICAL_ICONS: Record<CanonicalFeatureKey, React.ReactNode> = {
  sms: <MessageSquare className="h-3.5 w-3.5 text-slate-400 shrink-0" />,
  email: <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />,
  ai: <Sparkles className="h-3.5 w-3.5 text-violet-400 shrink-0" />,
  users: <Users className="h-3.5 w-3.5 text-slate-400 shrink-0" />,
  contacts: <Contact className="h-3.5 w-3.5 text-slate-400 shrink-0" />,
  forms: <FileInput className="h-3.5 w-3.5 text-slate-400 shrink-0" />,
};

function CanonicalFeaturesPreview({
  watch,
  register,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watch: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any;
}) {
  const limits = {
    smsCreditsPerMonth: Number(watch('smsCreditsPerMonth') ?? 0),
    emailCreditsPerMonth: Number(watch('emailCreditsPerMonth') ?? 0),
    aiCreditsPerMonth: Number(watch('aiCreditsPerMonth') ?? 0),
    maxUsers: Number(watch('maxUsers') ?? 0),
    maxContacts: Number(watch('maxContacts') ?? 0),
    maxForms: Number(watch('maxForms') ?? 0),
  };

  const visible = CANONICAL_ORDER.filter((k) => shouldShowCanonical(k, limits));

  return (
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
        Auto-Populated Core Features
      </p>
      {visible.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">
          Set a usage limit above to auto-populate core features.
        </p>
      ) : (
        <div className="space-y-2">
          {visible.map((key) => {
            const value = formatCanonicalValue(key, limits);
            return (
              <div
                key={key}
                className="flex items-center gap-3 rounded-md border border-slate-200/70 dark:border-slate-700/70 bg-slate-50/40 dark:bg-slate-800/20 p-2.5"
              >
                <div className="shrink-0">{CANONICAL_ICONS[key]}</div>
                <div className="flex-1 min-w-0">
                  <Input
                    placeholder={CANONICAL_DEFAULT_LABELS[key]}
                    {...register(`labelOverrides.${key}`)}
                    className="h-8 text-sm bg-white dark:bg-slate-900"
                  />
                </div>
                <div className="shrink-0 min-w-[140px] text-right">
                  <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                    {value}
                  </span>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Auto · tied to limit
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================
// Component
// ============================================

export function PlanForm({
  plan,
  onSubmit,
  onCancel,
  isLoading,
}: PlanFormProps) {
  const isEditing = !!plan;
  const [submitError, setSubmitError] = useState<string | null>(null);

  const storedFeatures = plan
    ? parseStoredFeatures(plan.features)
    : { custom: [], labelOverrides: {} };

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isDirty },
  } = useForm<PlanFormData>({
    resolver: zodResolver(planFormSchema),
    defaultValues: {
      name: plan?.name || '',
      description: plan?.description || '',
      monthlyPrice: plan?.monthlyPrice || 0,
      annualPrice: plan?.annualPrice || 0,
      trialDays: plan?.trialDays ?? 0,
      smsCreditsPerMonth: plan?.smsCreditsPerMonth ?? 100,
      emailCreditsPerMonth: plan?.emailCreditsPerMonth ?? 500,
      aiCreditsPerMonth: plan?.aiCreditsPerMonth ?? 0,
      maxUsers: plan?.maxUsers ?? 1,
      maxContacts: plan?.maxContacts ?? 100,
      maxForms: plan?.maxForms ?? 1,
      maxSmsPerBroadcast: plan?.maxSmsPerBroadcast ?? 10,
      maxEmailPerBroadcast: plan?.maxEmailPerBroadcast ?? 25,
      customFeatures: storedFeatures.custom.map((c) => ({
        label: c.label,
        value: c.value ?? '',
      })),
      labelOverrides: {
        sms: storedFeatures.labelOverrides?.sms ?? '',
        email: storedFeatures.labelOverrides?.email ?? '',
        ai: storedFeatures.labelOverrides?.ai ?? '',
        users: storedFeatures.labelOverrides?.users ?? '',
        contacts: storedFeatures.labelOverrides?.contacts ?? '',
        forms: storedFeatures.labelOverrides?.forms ?? '',
      },
      isActive: plan?.isActive ?? true,
      displayOrder: plan?.displayOrder || 0,
      tierLevel: plan?.tierLevel ?? 0,
      highlightLabel: plan?.highlightLabel ?? '',
      badgeColor: plan?.badgeColor ?? '',
      recommendedForIndividual: plan?.recommendedForIndividual ?? false,
      recommendedForBusiness: plan?.recommendedForBusiness ?? false,
      requireCardForTrial: plan?.requireCardForTrial ?? false,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'customFeatures',
  });

  const watchMonthlyPrice = watch('monthlyPrice');
  const watchAnnualPrice = watch('annualPrice');

  const annualSavings =
    watchMonthlyPrice && watchAnnualPrice
      ? watchMonthlyPrice * 12 - watchAnnualPrice
      : 0;
  const savingsPercent =
    watchMonthlyPrice > 0
      ? Math.round((annualSavings / (watchMonthlyPrice * 12)) * 100)
      : 0;

  const onFormSubmit = async (data: PlanFormData) => {
    setSubmitError(null);
    try {
      const dto: CreatePlanDto = {
        name: data.name,
        description: data.description,
        monthlyPrice: data.monthlyPrice,
        annualPrice: data.annualPrice,
        trialDays: data.trialDays,
        smsCreditsPerMonth: data.smsCreditsPerMonth,
        emailCreditsPerMonth: data.emailCreditsPerMonth,
        aiCreditsPerMonth: data.aiCreditsPerMonth,
        maxUsers: data.maxUsers,
        maxContacts: data.maxContacts,
        maxForms: data.maxForms,
        maxSmsPerBroadcast: data.maxSmsPerBroadcast,
        maxEmailPerBroadcast: data.maxEmailPerBroadcast,
        features: stringifyStoredFeatures({
          labelOverrides: data.labelOverrides,
          custom: data.customFeatures
            .filter((c) => c.label.trim())
            .map((c) => ({
              label: c.label.trim(),
              ...(c.value && c.value.trim()
                ? { value: c.value.trim() }
                : {}),
            })),
        }),
        isActive: data.isActive,
        displayOrder: data.displayOrder,
        tierLevel: data.tierLevel,
        highlightLabel: data.highlightLabel || null,
        badgeColor: data.badgeColor || null,
        recommendedForIndividual: data.recommendedForIndividual,
        recommendedForBusiness: data.recommendedForBusiness,
        requireCardForTrial: data.requireCardForTrial,
      };
      const success = await onSubmit(dto);
      if (!success) {
        setSubmitError('Failed to save plan. Please try again.');
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    }
  };

  const iconCls = 'h-3.5 w-3.5 text-slate-400 shrink-0';

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Basic Info */}
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-slate-500" />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Plan Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Professional"
                {...register('name')}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-xs text-red-500">{errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayOrder">Display Order</Label>
              <Input
                id="displayOrder"
                type="number"
                min="0"
                placeholder="0"
                {...register('displayOrder')}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe what this plan offers..."
              rows={3}
              {...register('description')}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <div>
              <Label htmlFor="isActive" className="font-medium">
                Active
              </Label>
              <p className="text-xs text-slate-500">
                Inactive plans are hidden from new subscribers
              </p>
            </div>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <Switch
                  id="isActive"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-slate-500" />
            Pricing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="monthlyPrice">Monthly Price (USD) *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  $
                </span>
                <Input
                  id="monthlyPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  className={cn('pl-7', errors.monthlyPrice && 'border-red-500')}
                  {...register('monthlyPrice')}
                />
              </div>
              {errors.monthlyPrice && (
                <p className="text-xs text-red-500">{errors.monthlyPrice.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="annualPrice">Annual Price (USD)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  $
                </span>
                <Input
                  id="annualPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  className="pl-7"
                  {...register('annualPrice')}
                />
              </div>
              {savingsPercent > 0 && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400">
                  {savingsPercent}% savings vs monthly
                </p>
              )}
            </div>
          </div>

          {/* Trial — drives card-required signup OOBE flow. 0 = no trial */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="trialDays">Trial Days</Label>
              <Input
                id="trialDays"
                type="number"
                min="0"
                max="90"
                step="1"
                className={cn(errors.trialDays && 'border-red-500')}
                {...register('trialDays')}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                0 = charge immediately on card capture. Drives the trial badge in the signup plan picker.
              </p>
              {errors.trialDays && (
                <p className="text-xs text-red-500">{errors.trialDays.message}</p>
              )}
            </div>
          </div>

          {/* Require card for trial — only meaningful when trialDays > 0 */}
          {(watch('trialDays') ?? 0) > 0 && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 p-3">
              <div>
                <Label htmlFor="requireCardForTrial" className="font-medium">
                  Require card for trial
                </Label>
                <p className="text-xs text-slate-500">
                  Off (default): the trial starts in signup without a card. On: users must add a card before the trial begins.
                </p>
              </div>
              <Controller
                name="requireCardForTrial"
                control={control}
                render={({ field }) => (
                  <Switch
                    id="requireCardForTrial"
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage Limits */}
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-500" />
              Usage Limits
            </CardTitle>
            <p className="text-xs text-slate-400">Use 0 to disable a feature</p>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Messaging */}
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
              Messaging
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <LimitField id="smsCreditsPerMonth" label="SMS Credits/Month" icon={<MessageSquare className={iconCls} />} register={register} error={errors.smsCreditsPerMonth?.message} />
              <LimitField id="emailCreditsPerMonth" label="Email Credits/Month" icon={<Mail className={iconCls} />} register={register} error={errors.emailCreditsPerMonth?.message} />
              <LimitField id="aiCreditsPerMonth" label="AI Credits/Month" icon={<Sparkles className={cn(iconCls, 'text-violet-400')} />} register={register} />
            </div>
          </div>

          <div className="border-t border-slate-100 dark:border-slate-800" />

          {/* Team & Contacts */}
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
              Team &amp; Contacts
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <LimitField id="maxUsers" label="Team Members (Users)" icon={<Users className={iconCls} />} register={register} />
              <LimitField id="maxContacts" label="Max Contacts" icon={<Contact className={iconCls} />} register={register} />
              <LimitField id="maxForms" label="Max Forms" icon={<FileInput className={iconCls} />} register={register} />
            </div>
          </div>

          <div className="border-t border-slate-100 dark:border-slate-800" />

          {/* Broadcasting */}
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
              Broadcasting
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <LimitField id="maxSmsPerBroadcast" label="Max SMS per Broadcast" icon={<Send className={iconCls} />} register={register} />
              <LimitField id="maxEmailPerBroadcast" label="Max Emails per Broadcast" icon={<Mail className={iconCls} />} register={register} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features */}
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-slate-500" />
            Features
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Core features auto-populate from the usage limits above. Edit the
            display label or add custom extras below.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <CanonicalFeaturesPreview
            watch={watch}
            register={register}
          />

          <div className="border-t border-slate-100 dark:border-slate-800" />

          {/* Custom Features */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Custom Features
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Extras shown alongside the auto-populated core features.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => append({ label: '', value: '' })}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Feature
              </Button>
            </div>
            {fields.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                No custom features. Click &quot;Add Feature&quot; to add one.
              </p>
            ) : (
              <div className="space-y-2">
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-start gap-2">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Feature name (e.g., Priority Onboarding)"
                        {...register(`customFeatures.${index}.label`)}
                        className={
                          errors.customFeatures?.[index]?.label
                            ? 'border-red-500'
                            : ''
                        }
                      />
                      <Input
                        placeholder="Sub-label (optional, e.g., 99.9% uptime)"
                        {...register(`customFeatures.${index}.value`)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(index)}
                      className="text-slate-400 hover:text-red-500 mt-0.5"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Display & Recommendations */}
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-slate-500" />
            Display &amp; Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="tierLevel">Tier Level</Label>
              <Input
                id="tierLevel"
                type="number"
                min="0"
                max="3"
                {...register('tierLevel')}
                className="h-8 text-sm"
              />
              <p className="text-xs text-slate-400">0=Free, 1=Standard, 2=Pro, 3=Enterprise</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="highlightLabel">Highlight Label</Label>
              <Input
                id="highlightLabel"
                placeholder="e.g., Most Popular"
                {...register('highlightLabel')}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="badgeColor">Badge Color</Label>
              <Controller
                name="badgeColor"
                control={control}
                render={({ field }) => {
                  const value = field.value || '';
                  const isValidHex = /^#[0-9A-Fa-f]{6}$/.test(value);
                  const swatch = isValidHex ? value : '#3B82F6';
                  return (
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="badgeColor"
                        className="relative h-8 w-8 shrink-0 cursor-pointer rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden"
                        style={{ backgroundColor: swatch }}
                        aria-label="Pick badge color"
                      >
                        <input
                          id="badgeColor"
                          type="color"
                          value={swatch}
                          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        />
                      </label>
                      <Input
                        value={value}
                        placeholder="#3B82F6"
                        onChange={(e) => field.onChange(e.target.value)}
                        className="h-8 text-sm font-mono uppercase"
                      />
                      {value && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => field.onChange('')}
                          className="h-8 px-2 text-slate-400 hover:text-red-500"
                          aria-label="Clear badge color"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  );
                }}
              />
              {errors.badgeColor && (
                <p className="text-xs text-red-500">{errors.badgeColor.message}</p>
              )}
            </div>
          </div>

          <div className="border-t border-slate-100 dark:border-slate-800" />

          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
              OOBE Recommendations
            </p>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                <div>
                  <Label htmlFor="recommendedForIndividual" className="font-medium">
                    Recommend for Individual accounts
                  </Label>
                  <p className="text-xs text-slate-500">
                    Show this as the recommended plan when account type is &quot;Individual&quot;
                  </p>
                </div>
                <Controller
                  name="recommendedForIndividual"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      id="recommendedForIndividual"
                      checked={field.value ?? false}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                <div>
                  <Label htmlFor="recommendedForBusiness" className="font-medium">
                    Recommend for Business accounts
                  </Label>
                  <p className="text-xs text-slate-500">
                    Show this as the recommended plan when account type is &quot;Business&quot;
                  </p>
                </div>
                <Controller
                  name="recommendedForBusiness"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      id="recommendedForBusiness"
                      checked={field.value ?? false}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Message */}
      {submitError && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
          <p className="text-sm text-red-700 dark:text-red-400">{submitError}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
        <Button type="button" variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-1.5" />
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading || (!isDirty && isEditing)}>
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-1.5" />
              {isEditing ? 'Save Changes' : 'Create Plan'}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
